/**
 * FileUploadCenter — iter-236 (Feb 2026)
 * Path: /control/files (linked from ControlPanelSettings.js)
 * Purpose: Admin file drop-zone. Any file → Emergent Object Storage,
 *          metadata in Mongo, direct download link auto-copied.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Upload, Loader2, Trash2, Copy, Download, FileText, Search, X,
} from 'lucide-react';

import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { Input } from '../components/ui/input';
import { getAuthHeaders } from '../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function humanSize(n) {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

export default function FileUploadCenter() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (a, e) => (isAr ? a : e);

  const [files, setFiles]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [drag, setDrag]       = useState(false);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/files/`, { headers: getAuthHeaders() });
      setFiles(r.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل تحميل الملفات' : 'Failed to load files'));
    } finally {
      setLoading(false);
    }
    // Same reason as MobileAppsControl — `t` is unstable so we exclude it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (file, label) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (label) fd.append('label', label);
      const r = await axios.post(`${BACKEND_URL}/api/admin/files/upload`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t(`تم رفع "${file.name}"`, `Uploaded "${file.name}"`));
      await load();
      // Auto-copy download link
      const url = `${BACKEND_URL}${r.data.download_url}`;
      try { await navigator.clipboard.writeText(url); toast.success(t('تم نسخ رابط التحميل', 'Download link copied')); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الرفع', 'Upload failed'));
    } finally { setBusy(false); }
  };

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const copyLink = async (fileId) => {
    const url = `${BACKEND_URL}/api/admin/files/${fileId}/download`;
    try { await navigator.clipboard.writeText(url); toast.success(t('تم النسخ', 'Copied')); }
    catch { toast.error(t('فشل النسخ', 'Copy failed')); }
  };

  const remove = async (fileId, name) => {
    if (!window.confirm(t(`حذف "${name}"؟`, `Delete "${name}"?`))) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/files/${fileId}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      setFiles(f => f.filter(x => x.id !== fileId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/files/${fileId}/download`, {
        headers: getAuthHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('فشل التحميل', 'Download failed'));
    }
  };

  const filtered = files.filter(f =>
    !search.trim() ||
    (f.original_filename || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.label || '').toLowerCase().includes(search.toLowerCase())
  );

  const breadcrumb = [
    { labelAr: 'الرئيسية', labelEn: 'Home', path: '/dashboard' },
    { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
    { labelAr: 'رفع الملفات', labelEn: 'File Uploads' },
  ];

  return (
    <DashboardLayout title="File Uploads" onBack={() => navigate('/control-panel-settings')} breadcrumb={breadcrumb}>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="file-upload-center">
        {/* Drop zone */}
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={
            'p-8 rounded-2xl border-2 border-dashed transition-all text-center ' +
            (drag
              ? 'bg-[#d4af37]/10 border-[#d4af37] scale-[1.01]'
              : 'bg-white/[0.02] border-white/15 hover:border-[#d4af37]/40')
          }
          data-testid="file-drop-zone"
        >
          <Upload className="w-10 h-10 mx-auto mb-3 text-[#d4af37]" />
          <div className="text-white font-semibold mb-1">
            {t('اسحب وأفلت أي ملف هنا', 'Drag & drop any file here')}
          </div>
          <div className="text-xs text-white/50 mb-4">
            {t('أو اختر من جهازك — حتى 50 ميجابايت', 'Or pick from your device — up to 50 MB')}
          </div>
          <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#d4af37] hover:bg-[#c9a227] text-black font-bold cursor-pointer transition disabled:opacity-50">
            <input type="file" onChange={onFileInput} disabled={busy} className="hidden" data-testid="file-input" />
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? t('جاري الرفع…', 'Uploading…') : t('اختر ملف', 'Choose file')}
          </label>
        </motion.div>

        {/* Search */}
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('ابحث بالاسم أو التسمية…', 'Search by name or label…')}
            className="bg-white/[0.03] border-white/10 text-white rounded-xl h-11"
            data-testid="files-search"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="text-white/40 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* File list */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-12 text-white/50">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-white/40 text-sm">
              {search ? t('لا توجد نتائج', 'No matches') : t('لا توجد ملفات مرفوعة بعد', 'No files uploaded yet')}
            </div>
          ) : filtered.map((f) => (
            <div key={f.id}
              className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] transition"
              data-testid={`file-row-${f.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#d4af37]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate" title={f.original_filename}>
                  {f.label && f.label !== f.original_filename ? `${f.label} · ` : ''}{f.original_filename}
                </div>
                <div className="text-[11px] text-white/40 font-mono flex gap-3 mt-0.5" dir="ltr">
                  <span>{humanSize(f.size)}</span>
                  <span>{f.content_type}</span>
                  <span>{f.created_at ? new Date(f.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB') : ''}</span>
                </div>
              </div>
              <button type="button" onClick={() => copyLink(f.id)}
                title={t('نسخ رابط التحميل', 'Copy download link')}
                className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white"
                data-testid={`file-copy-${f.id}`}>
                <Copy className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => downloadFile(f.id, f.original_filename)}
                title={t('تحميل', 'Download')}
                className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white"
                data-testid={`file-download-${f.id}`}>
                <Download className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => remove(f.id, f.original_filename)}
                title={t('حذف', 'Delete')}
                className="p-2 rounded-lg hover:bg-rose-500/20 text-rose-300"
                data-testid={`file-delete-${f.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
